const PictViewClass = require('pict-view');

/** @typedef {import('./HeadlightPanels-View.js').Pict} Pict */

/**
 * The "Current" tab of the Samples panel: the Samples joined to the document being edited.
 *
 * Selecting a row opens the existing Sample Details panel on the RIGHT while this list stays open on
 * the left, so the list and the selected sample's Details/Location/Logs/Print tabs are visible together.
 * That split is why `sampledetails` is configured `location: "right"` (mirroring observations /
 * observationdetails) rather than replacing this panel.
 */
class SampleCurrent extends PictViewClass
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;

		/** @type {Array<Object>} Samples joined to the current document. */
		this.CurrentSamples = [];
		/** @type {number|string|null} The row currently open in the Sample Details panel. */
		this.ActiveIDSample = null;
		/** @type {'loading'|'items'|'empty'} Drives which of the three list states the template paints. */
		this.ListState = 'loading';
	}

	/**
	 * Run when the Current tab is activated: load the per-customer print configuration (once) and
	 * refresh the list. Deliberately not done at panel render time — a document whose user never opens
	 * this tab should not pay for either.
	 * @return {Promise<void>}
	 * @async
	 */
	async handleTabOpened()
	{
		await this.pict.providers.QRCodeProvider.ensurePrintableManifests();
		await this.refreshCurrentSamples();
	}

	/**
	 * Fetch the Samples joined to the current document and repaint the list.
	 * @return {Promise<void>}
	 * @async
	 */
	async refreshCurrentSamples()
	{
		const tmpIDDocument = this.pict.AppData?.DocumentData?.ReportData?.IDDocument;
		if (!tmpIDDocument)
		{
			this.CurrentSamples = [];
		}
		else
		{
			try
			{
				const tmpSamples = await this.pict.providers.SampleProvider.getDocumentJoinedSamples(tmpIDDocument);
				// Sample.Name is very often just the QR code again; only carry it when it adds something.
				this.CurrentSamples = tmpSamples.map((pSample) => Object.assign({}, pSample,
					{ _DisplayName: (pSample.Name && pSample.Name !== pSample.QRCode) ? pSample.Name : '' }));
			}
			catch (pError)
			{
				this.log.error(`Failed to load the Samples joined to document ${tmpIDDocument}.`, pError);
				this.CurrentSamples = [];
			}
		}
		this.ListState = this.CurrentSamples.length > 0 ? 'items' : 'empty';
		return new Promise((resolve) =>
		{
			this.renderAsync(() =>
			{
				this._paintActiveRow();
				resolve();
			});
		});
	}

	/**
	 * Open a Sample in the Sample Details panel (right side) and mark its row active here.
	 * @param {Event} event - The click event
	 * @param {number|string} pIDSample - The Sample to open
	 * @return {void}
	 */
	selectSample(event, pIDSample)
	{
		if (!pIDSample)
		{
			return;
		}
		this.ActiveIDSample = pIDSample;
		this._paintActiveRow();
		const tmpSampleRecord = this.CurrentSamples.find((pSample) => String(pSample.IDSample) === String(pIDSample));
		// so the Details panel's back button returns here rather than to Search
		this.pict.views.SampleDetails.SampleDetailsBackTo = 'Current';
		this.pict.views.SampleDetails.showSampleDetailView(pIDSample, tmpSampleRecord, 'Details');
	}

	/**
	 * Print the QR label for a Sample. Same call the Sample Details Print tab uses, minus the preview
	 * container -- omitting it sends the label to the printer instead of rendering it inline.
	 * @param {Event} event - The click event
	 * @param {string} pQRCode - The Sample QR code
	 * @return {void}
	 */
	printSample(event, pQRCode)
	{
		if (event)
		{
			// the row itself opens the details panel; printing should not also do that
			event.stopPropagation();
		}
		if (!pQRCode)
		{
			return;
		}
		this.pict.providers.QRCodeProvider.printDecoratedEntity('Sample', pQRCode);
	}

	/**
	 * Open a Sample straight to its Print tab, where the label preview renders.
	 * @param {Event} event - The click event
	 * @param {number|string} pIDSample - The Sample to preview
	 * @return {void}
	 */
	previewSample(event, pIDSample)
	{
		this._openSampleOnTab(event, pIDSample, 'Print');
	}

	/**
	 * Open a Sample straight to its Logs tab.
	 * @param {Event} event - The click event
	 * @param {number|string} pIDSample - The Sample to open
	 * @return {void}
	 */
	openSampleLogs(event, pIDSample)
	{
		this._openSampleOnTab(event, pIDSample, 'Logs');
	}

	/**
	 * Shared row-action path: mark the row active and open Sample Details on a given tab.
	 * @param {Event} event - The click event
	 * @param {number|string} pIDSample - The Sample to open
	 * @param {string} pTab - The Sample Details tab to open
	 * @return {void}
	 */
	_openSampleOnTab(event, pIDSample, pTab)
	{
		if (event)
		{
			// the row itself opens the Details tab; a row-action button picks its own
			event.stopPropagation();
		}
		if (!pIDSample)
		{
			return;
		}
		this.ActiveIDSample = pIDSample;
		this._paintActiveRow();
		const tmpSampleRecord = this.CurrentSamples.find((pSample) => String(pSample.IDSample) === String(pIDSample));
		this.pict.views.SampleDetails.SampleDetailsBackTo = 'Current';
		this.pict.views.SampleDetails.showSampleDetailView(pIDSample, tmpSampleRecord, pTab);
	}

	/**
	 * Move the `is-active` highlight onto the row for ActiveIDSample. Re-applied after every render,
	 * since rendering replaces the row elements.
	 * @return {void}
	 */
	_paintActiveRow()
	{
		if (typeof document === 'undefined')
		{
			return;
		}
		const tmpRows = document.querySelectorAll('[data-sample-current-id]');
		tmpRows.forEach((pRow) =>
		{
			const tmpIsActive = this.ActiveIDSample != null &&
				pRow.getAttribute('data-sample-current-id') === String(this.ActiveIDSample);
			pRow.classList.toggle('is-active', tmpIsActive);
		});
	}
}

module.exports = SampleCurrent;

module.exports.default_configuration =
{
	ViewIdentifier: 'SampleCurrent',
	DefaultRenderable: 'SampleCurrent-List',
	DefaultDestinationAddress: '#SampleCurrent-Container',
	Templates:
	[
		{
			Hash: 'SampleCurrent-List',
			Template: /*html*/`
			<div id="SampleCurrent-List">
				{~TIfAbs:SampleCurrent-Loading:Context[0]:Context[0].ListState^===^loading~}
				{~TIfAbs:SampleCurrent-Items:Context[0].CurrentSamples:Context[0].ListState^===^items~}
				{~TIfAbs:SampleCurrent-Empty:Context[0]:Context[0].ListState^===^empty~}
			</div>`,
		},
		{
			Hash: 'SampleCurrent-Loading',
			Template: /*html*/`
			<div class="panel-block is-block-flex is-align-items-left p-5">
				<div class="has-text-centered has-text-text-75" style="flex-grow: 1">
					<span class="icon"><i class="fa fa-fw fa-spinner fa-pulse"></i></span>
					<span class="icon-text">Loading samples&hellip;</span>
				</div>
			</div>`,
		},
		{
			Hash: 'SampleCurrent-Empty',
			Template: /*html*/`
			<div class="panel-block is-block-flex is-align-items-left p-5">
				<div class="has-text-centered" style="flex-grow: 1">
					<span class="icon"><i class="fa fa-fw fa-vial"></i></span>
					<span class="icon-text">No samples are attached to this document yet.</span>
				</div>
			</div>`,
		},
		{
			Hash: 'SampleCurrent-Items',
			Template: /*html*/`
			<div id="SampleCurrent-Items" class="nested-scroll-content">
				{~TS:SampleCurrent-Item:Record~}
			</div>`,
		},
		{
			Hash: 'SampleCurrent-Item',
			Template: /*html*/`
			<div class="panel-block is-block-flex is-align-items-left is-relative p-0 is-clickable"
				data-sample-current-id="{~D:Record.IDSample~}"
				title="Open this sample's details"
				onclick="_Pict.views.SampleCurrent.selectSample(event, '{~D:Record.IDSample~}')">
				<div class="is-block-flex is-align-items-left mx-4 my-2" style="flex-grow: 1">
					<div>
						<span class="icon"><i class="fas fa-fw fa-qrcode"></i></span>
						<b>{~D:Record.QRCode~}</b>
						<span class="has-text-text-75">{~D:Record._DisplayName~}</span>
					</div>
					<div class="is-size-7 has-text-text-75">{~StatusPill:Record.Status~}</div>
					<div class="is-size-7 has-text-text-75"><b>Material:</b> {~Entity:Material^Record.IDMaterial^SampleCurrent-Item-Material^~}</div>
					<div class="is-size-7 has-text-text-75"><b>Project:</b> {~Entity:Project^Record.IDProject^SampleCurrent-Item-Project^~}</div>
					<div class="is-size-7 has-text-text-75">
						<span class="has-text-text-60"><b class="has-text-text-80">Created</b> on</span>
						<span class="has-text-text-80">{~DateFormat:Record.CreateDate^MMMM DD, YYYY - hh:mm a^~}</span>
						<span class="has-text-text-60">by</span>
						<span class="has-text-text-80">{~Entity:User^Record.CreatingIDUser^SampleCurrent-Item-User^~}</span>
					</div>
				</div>
				<div class="buttons are-small mr-3 my-2">
					<button type="button" class="button is-soft is-primary" title="Open this sample's logs"
						onclick="_Pict.views.SampleCurrent.openSampleLogs(event, '{~D:Record.IDSample~}')">
						<span class="icon"><i class="fas fa-fw fa-list-timeline"></i></span>
					</button>
					<button type="button" class="button is-soft is-primary" title="Preview the QR code label"
						onclick="_Pict.views.SampleCurrent.previewSample(event, '{~D:Record.IDSample~}')">
						<span class="icon"><i class="fas fa-fw fa-print-magnifying-glass"></i></span>
					</button>
					<button type="button" class="button is-primary" title="Print the QR code label"
						onclick="_Pict.views.SampleCurrent.printSample(event, '{~D:Record.QRCode~}')">
						<span class="icon"><i class="fas fa-fw fa-print"></i></span>
					</button>
				</div>
			</div>`,
		},
		{
			Hash: 'SampleCurrent-Item-Material',
			Template: /*html*/`{~D:Record.Name:Unknown~}`,
		},
		{
			Hash: 'SampleCurrent-Item-Project',
			Template: /*html*/`{~D:Record.Name:Unknown~}`,
		},
		{
			Hash: 'SampleCurrent-Item-User',
			Template: /*html*/`{~D:Record.NameFirst~} {~D:Record.NameLast~}`,
		},
	],
	Renderables:
	[
		{
			RenderableHash: 'SampleCurrent-List',
			TemplateHash: 'SampleCurrent-List',
			DestinationAddress: '#SampleCurrent-Container',
		},
	],
};
